import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Drawer,
  FormControlLabel,
  IconButton,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { Close as CloseIcon, OpenInNew as OpenInNewIcon, ViewAgenda as ChunksIcon } from "@mui/icons-material";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import json from "react-syntax-highlighter/dist/esm/languages/hljs/json";
import { docco, atomOneDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import usePostRequest from "../../../hooks/usePostRequest";
import { INDIGO, SLATE } from "../../../constants/Colors";

// Register the JSON language
SyntaxHighlighter.registerLanguage("json", json);

// Component to display prettified JSON
const JsonViewer = ({ data }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [fontSize, setFontSize] = useState(13);
  const [copyNotification, setCopyNotification] = useState(false);

  if (!data) return <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>No data available</Typography>;

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopyNotification(true);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Toolbar */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCopyToClipboard}
            sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", fontSize: "0.8125rem", "&:hover": { borderColor: INDIGO[400], bgcolor: INDIGO[50], color: INDIGO[600] } }}
          >
            Copy JSON
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", border: `1px solid ${SLATE[200]}`, borderRadius: "8px", overflow: "hidden" }}>
            <Button
              size="small"
              onClick={() => setFontSize((prev) => Math.max(prev - 2, 10))}
              sx={{ minWidth: 36, color: SLATE[600], textTransform: "none", borderRadius: 0, fontSize: "0.8125rem", "&:hover": { bgcolor: SLATE[100] } }}
            >
              A−
            </Button>
            <Box sx={{ width: "1px", height: 20, bgcolor: SLATE[200] }} />
            <Button
              size="small"
              onClick={() => setFontSize((prev) => Math.min(prev + 2, 24))}
              sx={{ minWidth: 36, color: SLATE[600], textTransform: "none", borderRadius: 0, fontSize: "0.8125rem", "&:hover": { bgcolor: SLATE[100] } }}
            >
              A+
            </Button>
          </Box>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={darkMode}
              onChange={() => setDarkMode(!darkMode)}
              size="small"
              sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: INDIGO[600] }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: INDIGO[400] } }}
            />
          }
          label={<Typography sx={{ fontSize: "0.8125rem", color: SLATE[600] }}>{darkMode ? "Dark" : "Light"}</Typography>}
        />
      </Box>

      {/* Code block */}
      <Box
        sx={{
          bgcolor: darkMode ? "#1a1b26" : "#ffffff",
          border: `1px solid ${darkMode ? "#2a2b3d" : SLATE[200]}`,
          borderRadius: "10px",
          overflow: "auto",
          maxHeight: "calc(100vh - 220px)",
        }}
      >
        <SyntaxHighlighter
          language="json"
          style={darkMode ? atomOneDark : docco}
          customStyle={{
            margin: 0,
            padding: "16px",
            background: "transparent",
            fontSize: `${fontSize}px`,
            lineHeight: "1.6",
            fontFamily: "ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, Consolas, monospace",
          }}
          wrapLines
          wrapLongLines
          showLineNumbers
        >
          {JSON.stringify(data, null, 2)}
        </SyntaxHighlighter>
      </Box>

      <Snackbar
        open={copyNotification}
        autoHideDuration={3000}
        onClose={(_, reason) => { if (reason !== "clickaway") setCopyNotification(false); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>JSON copied to clipboard!</Alert>
      </Snackbar>
    </Box>
  );
};

export default function ChunksSidebar({
  url,
  isOpen,
  onClose,
  currentApp,
  tokens,
  website,
}) {
  const [chunks, setChunks] = useState(null);
  const [error, setError] = useState(null);

  // Use the post request hook - using the same pattern as bulk_create
  const { sendPostRequest, loading } = usePostRequest(
    `/v1/apps/${currentApp.id}/websites/${website.id}/urls/fetch_cached_chunks`,
    tokens
  );

  const fetchChunks = async () => {
    if (!url || !url.url) return;

    setError(null);
    setChunks(null);

    try {
      // Make POST request with appId and url
      const response = await sendPostRequest({
        appId: currentApp.id,
        url: url.url,
      });

      if (response.error) {
        throw new Error(response.error);
      }

      setChunks(response);
    } catch (err) {
      console.error("Failed to fetch chunks:", err);
      setError(err.message || "Failed to fetch chunks");
    }
  };

  useEffect(() => {
    if (isOpen && url && url.url) {
      fetchChunks();
    }
  }, [isOpen, url]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      sx={{ zIndex: 21474836 }}
      PaperProps={{
        sx: {
          width: "70%",
          minWidth: 640,
          maxWidth: 1100,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: SLATE[50],
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#ffffff",
          borderBottom: `1px solid ${SLATE[200]}`,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1, mr: 2 }}>
          <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.06em", mb: "6px" }}>
            Knowledge Base — Chunks
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <ChunksIcon sx={{ color: INDIGO[500], fontSize: "1rem", flexShrink: 0 }} />
            <Typography
              component="a"
              href={url?.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: "1rem",
                fontWeight: 700,
                color: SLATE[900],
                textDecoration: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&:hover": { color: INDIGO[600], textDecoration: "underline" },
              }}
              title={url?.url}
            >
              {url?.url}
            </Typography>
            <OpenInNewIcon sx={{ fontSize: "0.875rem", color: SLATE[400], flexShrink: 0 }} />
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: SLATE[400], "&:hover": { color: SLATE[700], bgcolor: SLATE[100] }, mt: "2px" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        {loading ? (
          <LoadingSpinner message="Loading chunks..." subMessage="Fetching cached chunks for this URL" />
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography sx={{ fontSize: "2rem", mb: 1 }}>🔍</Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900], mb: "6px" }}>
              No chunks found
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
              {error}
            </Typography>
          </Box>
        ) : (
          <JsonViewer data={chunks} />
        )}
      </Box>
    </Drawer>
  );
}
